// ACC.B5 (#419) — Dev Agent self-correction attempt card. Renders one
// regeneration attempt as a structured row in the chat. Mounts above
// the DevReflectionCard summary, in chronological order. Rendered both
// from live SSE events (`dev_reflection_attempt_created`) and from
// agent_messages rows on session reload.
//
// Read-only — agent-generated, no editing path. The attempt records
// are produced by the 3-attempt self-correction loop in
// dev-reflection.service.ts and persisted to agent_messages with
// message_type='dev_reflection_attempt'.

// Mirror of SELF_CORRECTION_MAX_ATTEMPTS in
// apps/api/src/services/dev-reflection.service.ts. Kept inline rather
// than imported across the apps/api ↔ apps/web boundary to avoid a new
// shared-module dependency for one constant.
const SELF_CORRECTION_MAX_ATTEMPTS_DISPLAY = 3;

export interface AttemptCardContent {
  attemptNumber: number;
  issuesIdentified: {
    shortcutsIdentified: string[];
    scopeAdditions:      string[];
  };
  correctionMade: string;
  result: 'resolved' | 'continuing' | 'escalated';
}

interface AttemptCardProps {
  attempt: AttemptCardContent;
}

const RESULT_LABEL: Record<AttemptCardContent['result'], string> = {
  resolved:   'Resolved',
  continuing: 'Continuing',
  escalated:  'Escalated',
};

const RESULT_COLORS: Record<
  AttemptCardContent['result'],
  { bg: string; border: string; text: string; accent: string }
> = {
  resolved: {
    bg:     'rgba(34, 197, 94, 0.10)',
    border: 'rgba(34, 197, 94, 0.35)',
    text:   'rgb(22, 163, 74)',
    accent: 'rgb(34, 197, 94)',
  },
  continuing: {
    bg:     'rgba(100, 116, 139, 0.10)',
    border: 'rgba(100, 116, 139, 0.35)',
    text:   'rgb(71, 85, 105)',
    accent: 'rgb(100, 116, 139)',
  },
  escalated: {
    bg:     'rgba(239, 68, 68, 0.10)',
    border: 'rgba(239, 68, 68, 0.35)',
    text:   'rgb(185, 28, 28)',
    accent: 'rgb(239, 68, 68)',
  },
};

export default function AttemptCard({ attempt }: AttemptCardProps) {
  // Defensive lookup against an unrecognised `result` value (e.g. a
  // future loop variant that adds a new outcome before the UI catches
  // up, or a corrupted stored row). Falling back to the escalated
  // styling surfaces the row visibly rather than crashing the chat.
  const colors = RESULT_COLORS[attempt.result] ?? RESULT_COLORS.escalated;
  const label  = RESULT_LABEL[attempt.result] ?? attempt.result;
  // Defensive: guard against a malformed payload missing
  // `issuesIdentified`. partitionAgentMessages validates structure on
  // the restore path, but the SSE path trusts the server payload —
  // belt-and-suspenders against schema drift.
  const issues = attempt.issuesIdentified ?? { shortcutsIdentified: [], scopeAdditions: [] };
  const { shortcutsIdentified, scopeAdditions } = issues;
  const hasIssues = shortcutsIdentified.length > 0 || scopeAdditions.length > 0;

  return (
    <div
      data-testid="attempt-card"
      data-attempt-number={attempt.attemptNumber}
      data-attempt-result={attempt.result}
      style={{
        borderRadius: 'var(--radius-sm)',
        background:   'var(--surface)',
        border:       '1px solid var(--border)',
        borderLeft:   `3px solid ${colors.accent}`,
        padding:      14,
        marginTop:    6,
        marginBottom: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily:     'var(--font-body)',
              fontSize:       11,
              fontWeight:     600,
              color:          'var(--primary)',
              textTransform:  'uppercase',
              letterSpacing:  '0.05em',
            }}
          >
            Self-correction
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize:   14,
              fontWeight: 600,
              color:      'var(--text)',
              marginTop:  2,
            }}
          >
            Attempt {attempt.attemptNumber}/{SELF_CORRECTION_MAX_ATTEMPTS_DISPLAY}
          </div>
        </div>
        <span
          data-testid="attempt-result-badge"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize:   11,
            fontWeight: 600,
            padding:    '3px 8px',
            borderRadius: 12,
            background: colors.bg,
            border:     `1px solid ${colors.border}`,
            color:      colors.text,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Section
          heading="Issue identified"
          testId="attempt-issues"
          accent={colors.accent}
        >
          {hasIssues ? (
            <>
              {shortcutsIdentified.length > 0 && (
                <BulletList items={shortcutsIdentified} dotColor="rgb(245, 158, 11)" />
              )}
              {scopeAdditions.length > 0 && (
                <BulletList items={scopeAdditions} dotColor="rgb(59, 130, 246)" />
              )}
            </>
          ) : (
            <EmptyText>No specific issues recorded for this attempt.</EmptyText>
          )}
        </Section>

        <Section heading="Correction made" testId="attempt-correction" accent={colors.accent}>
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize:   13,
              color:      'var(--text)',
              lineHeight: 1.5,
            }}
          >
            {attempt.correctionMade}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({
  heading,
  testId,
  accent: _accent,
  children,
}: {
  heading: string;
  testId?: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div data-testid={testId}>
      <div
        style={{
          fontFamily:    'var(--font-body)',
          fontSize:      12,
          fontWeight:    600,
          color:         'var(--text)',
          marginBottom:  6,
        }}
      >
        {heading}
      </div>
      {children}
    </div>
  );
}

function BulletList({ items, dotColor }: { items: string[]; dotColor: string }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize:   13,
            color:      'var(--text)',
            paddingLeft: 14,
            position:   'relative',
            lineHeight: 1.5,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position:    'absolute',
              left:        2,
              top:         7,
              width:       6,
              height:      6,
              borderRadius: '50%',
              background:  dotColor,
            }}
          />
          {item}
        </li>
      ))}
    </ul>
  );
}

function EmptyText({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-body)',
        fontSize:   12,
        color:      'var(--text-secondary)',
        fontStyle:  'italic',
      }}
    >
      {children}
    </div>
  );
}
